import { q } from '../workspace/quote.js';
import { MAX_OUTPUT_CHARS, clipEnd } from '../mentor-context.js';
function need(p, key) {
    const v = p[key];
    if (typeof v !== 'string' || v.length === 0)
        throw new Error(`check parameter "${key}" is required`);
    return v;
}
function optStr(p, key, def) {
    const v = p[key];
    return typeof v === 'string' && v.length > 0 ? v : def;
}
function optNum(p, key) {
    const v = p[key];
    return typeof v === 'number' ? v : undefined;
}
function regex(p) {
    const pattern = need(p, 'pattern');
    const flags = optStr(p, 'flags', '');
    return new RegExp(pattern, flags);
}
async function currentBranch(ctx) {
    const r = await ctx.git('symbolic-ref -q --short HEAD');
    return r.code === 0 ? r.stdout.trim() : null;
}
/** File content either from the working tree or from `<ref>:<path>`. */
async function contentAt(ctx, path, ref) {
    if (ref) {
        const r = await ctx.git(`show ${q(`${ref}:${path}`)}`);
        return r.code === 0 ? r.stdout : null;
    }
    return ctx.readFile(path);
}
const where = (ref) => (ref ? `on "${ref}"` : 'in the working tree');
const NO_BASE = {
    ok: false,
    detail: 'This working copy has no "upstream" and no "origin" remote, so there is no repository for a pull request to live in.',
};
/** The path a remote points at, or null when there is no such remote. */
async function remotePath(ctx, remote) {
    const r = await ctx.git(`remote get-url ${q(remote)}`);
    const url = r.stdout.trim();
    return r.code === 0 && url.length > 0 ? url : null;
}
/**
 * The base repository, resolved the way `gh` does (docs/github.md, section 2):
 * `upstream` when that remote exists — the learner is on a fork — and `origin`
 * otherwise.
 *
 * The url is a path to a bare repository on the same disk. A relative one is
 * read from the mission's working directory, because that is where `ctx.exec`
 * runs and where the learner's own `git push` ran when it put the branch there.
 */
async function baseRepo(ctx) {
    for (const remote of ['upstream', 'origin']) {
        const path = await remotePath(ctx, remote);
        if (path !== null)
            return { remote, path };
    }
    return null;
}
/**
 * Every pull request in `dir`, in whatever order the glob lists them; the
 * callers pick by number, never by position.
 *
 * The guest hands over bytes and the parsing happens here: one `cat` of the
 * whole `pulls/` directory with commas between the files, so what comes back
 * is a single JSON array. jq will be in the image and could filter in the
 * guest, but a pull request body is free text a learner wrote — quotes,
 * brackets, newlines — and `JSON.parse` is the reader that cannot be talked
 * out of its answer by any of them. `pulls/next` is not `*.json`, so the glob
 * skips the counter without naming it.
 */
async function pullRequests(ctx, dir) {
    const cmd = `printf '['; n=0; for f in ${q(dir)}/pulls/*.json; do ` +
        `[ -f "$f" ] || continue; [ "$n" = 1 ] && printf ','; n=1; cat -- "$f"; ` +
        `done; printf ']'`;
    const r = await ctx.exec(cmd);
    if (r.code !== 0)
        return null;
    try {
        const parsed = JSON.parse(r.stdout);
        return Array.isArray(parsed) ? parsed : null;
    }
    catch {
        return null;
    }
}
/**
 * The check results the pre-receive hook recorded against a commit.
 *
 * It keeps them under the sha, in `pulls/checks/`, because a learner usually
 * pushes before there is a pull request to hang them on, and `gh` folds them
 * into the file the next time it is asked about one. Reading both here is
 * what keeps this a state check: a run that happened is a run that happened,
 * whether or not `gh pr view` has been typed since.
 */
async function shaChecks(ctx, dir, sha) {
    if (sha.length === 0)
        return [];
    const r = await ctx.exec(`cat -- ${q(`${dir}/pulls/checks/${sha}.json`)} 2>/dev/null`);
    if (r.code !== 0)
        return [];
    try {
        const parsed = JSON.parse(r.stdout);
        return Array.isArray(parsed.checks) ? parsed.checks : [];
    }
    catch {
        return [];
    }
}
/**
 * The pull request a check is about: the one in the base repository whose head
 * branch is `head`. When a head has been through more than one — a closed
 * attempt and the real one — the highest number is the current one.
 */
async function findPull(ctx, p) {
    const head = need(p, 'head');
    const base = await baseRepo(ctx);
    if (base === null)
        return NO_BASE;
    const all = await pullRequests(ctx, base.path);
    if (all === null)
        return { ok: false, detail: `The pull requests in "${base.remote}" could not be read as JSON.` };
    const matches = all.filter((pr) => pr.head === head);
    if (matches.length === 0)
        return { ok: false, detail: `No pull request for "${head}" exists in "${base.remote}".` };
    const pr = matches.reduce((a, b) => ((b.number ?? 0) >= (a.number ?? 0) ? b : a));
    return { ok: true, base, pr, matches };
}
/**
 * HTTP, in a sandbox with no network (docs/curriculum.md, section 3).
 *
 * The guest has no network device and will never get one (docs/browser.md,
 * decision 3), which makes a world about HTTP look impossible. It is not:
 * loopback is not a network device. A seed starts `node server.js &`, the
 * learner asks `127.0.0.1:8080` for something, and every request, status code
 * and timeout they meet is real — the same trick as the offline code host,
 * where the bare repository beside the working copy *is* the server.
 *
 * Three things about how these run are not incidental:
 *
 * The request is made **inside the workspace**, through `ctx.exec`, exactly as
 * `shell` runs a script. The grader is a separate process from the guest, and
 * in the browser it is on the other side of an emulator; a `fetch` from here
 * would leave from the host and arrive nowhere near the learner's server.
 *
 * The runner is **Node's own `fetch`**, because Node is in every image the
 * grader runs in. (curl is there too, and l66, l67 and l70 depend on it: their
 * port probes are `curl` in a `shell` check, because a node start costs two
 * seconds in the browser's VM.) It is piped in on stdin rather than written to a file, so a check
 * leaves the workspace byte-identical to how it found it — grading may read
 * state, never add to it.
 *
 * And every request is **bounded**. A hung server is a thing these levels
 * teach, so a hung server must not be able to hang grading.
 */
/**
 * How long one request may take before the probe gives up on it.
 *
 * The deadline lives in the guest, on the request itself, so hitting it
 * produces a failed check that says a timeout happened. Leaving it to
 * `ctx.exec`'s own 30-second kill would produce the same verdict thirty
 * seconds later with no diagnostic at all, which is the one outcome a learner
 * debugging a slow endpoint cannot use. `timeout_ms` raises it for a level
 * that means to wait, and is clamped well under the exec budget so the
 * outer kill stays what it is: an impossibility, not a fallback.
 */
const HTTP_TIMEOUT_MS = 5_000;
const HTTP_TIMEOUT_MIN = 250;
const HTTP_TIMEOUT_MAX = 20_000;
/**
 * Bytes of response body carried back across the wire. Generous for anything
 * a teaching service answers, and a cap rather than a stream because the body
 * travels home inside one line of a command's stdout.
 */
const HTTP_BODY_LIMIT = 64 * 1024;
/** The line the probe answers on, in the `@@KEY=` shape seeds already use. */
const HTTP_MARK = '@@HTTP=';
/**
 * A number that may have arrived as a string.
 *
 * `applyTemplate` (grader/index.ts) rewrites strings and only strings, so the
 * moment an author writes `status: "{{seed.EXPECTED_STATUS}}"` the value this
 * check receives is text. `optNum` would read that as "no expectation given"
 * and pass anything, so an unreadable value is an authoring error here rather
 * than a silently dropped assertion.
 */
function optInt(p, key) {
    const v = p[key];
    if (v === undefined || v === null)
        return undefined;
    if (typeof v === 'number' && Number.isFinite(v))
        return Math.trunc(v);
    if (typeof v === 'string' && /^\s*-?\d+\s*$/.test(v))
        return Number(v.trim());
    throw new Error(`check parameter "${key}" must be a number`);
}
/**
 * An address, split by hand.
 *
 * This package compiles against the language and nothing else — no Node
 * typings and no DOM (see its tsconfig), which is what keeps it runnable in a
 * browser tab — so `URL` is not a name it may use. That is no loss: the
 * addresses a mission writes are `127.0.0.1:8080/orders`, and the only parts
 * anything here needs are the host, to check it is loopback, and the whole
 * string, to hand to a fetch in the guest that will parse it properly anyway.
 */
function parseAddress(given) {
    const m = /^(?:([a-zA-Z][a-zA-Z0-9+.-]*):\/\/)?([^/?#]+)([^]*)$/.exec(given.trim());
    if (!m)
        return null;
    const host = m[2];
    // Credentials in the address would be a second thing to explain and a way
    // to smuggle an `@` past the host check; no level needs them.
    if (host.includes('@'))
        return null;
    const bracketed = /^\[([^\]]+)\](?::\d+)?$/.exec(host);
    const plain = /^([^:]+)(?::\d+)?$/.exec(host);
    const hostname = bracketed?.[1] ?? plain?.[1];
    if (hostname === undefined)
        return null;
    const scheme = (m[1] ?? 'http').toLowerCase();
    return { scheme, host, hostname: hostname.toLowerCase(), url: `${scheme}://${host}${m[3] ?? ''}` };
}
/**
 * Loopback only, and the fence is deliberate.
 *
 * The guest cannot reach anything else — but the local driver's workspace is a
 * directory on a developer's machine with a real network behind it. A level
 * graded against a host on the internet would pass on the author's laptop and
 * fail for every learner on earth. Refusing the address makes that a loud
 * failure on the first run of `check-missions.mjs` instead of a divergence
 * nobody sees until production, and it keeps a check from being a way to make
 * whichever machine is grading issue requests it did not choose.
 *
 * `0.0.0.0` is not loopback here even though Linux would route it there: it is
 * an address to bind, not one to call, and it does not connect on Windows,
 * where much of the authoring happens.
 */
function isLoopback(hostname) {
    if (hostname === 'localhost' || hostname.endsWith('.localhost'))
        return true;
    if (hostname === '::1' || hostname === '0:0:0:0:0:0:0:1')
        return true;
    return /^127(\.\d{1,3}){3}$/.test(hostname);
}
/**
 * The request body, which an author may write either way round.
 *
 * A string is sent exactly as written — that is how a level asks what the API
 * does with malformed JSON. Anything else is a YAML mapping the author wrote
 * as a mapping, serialised here, which also means the `{{seed.SKU}}` inside it
 * was templated along with every other string in `with:`.
 */
function requestBody(p) {
    const v = p.body;
    if (v === undefined || v === null)
        return undefined;
    return typeof v === 'string' ? v : JSON.stringify(v);
}
/** The three HTTP checks read the same parameters; this is that reading. */
function httpRequest(p) {
    const given = need(p, 'url');
    // The address an author writes is the address the learner types, and
    // `127.0.0.1:8080/health` has no scheme on it. Supplying one is friendlier
    // than demanding it, and it is the only scheme available in any case.
    const address = parseAddress(given);
    if (address === null)
        throw new Error(`check parameter "url" is not an address: ${given}`);
    if (address.scheme !== 'http')
        throw new Error(`check parameter "url" must be http; the sandbox has no certificate store, so https can never answer a learner`);
    if (!isLoopback(address.hostname)) {
        throw new Error(`check parameter "url" must point at loopback (127.0.0.1); the sandbox has no network device, so "${address.hostname}" can never answer a learner`);
    }
    const method = optStr(p, 'method', 'GET').trim().toUpperCase();
    const body = requestBody(p);
    if (body !== undefined && (method === 'GET' || method === 'HEAD')) {
        throw new Error(`a ${method} request cannot carry a "body"; name the method the ticket asks for`);
    }
    const asked = optInt(p, 'timeout_ms') ?? HTTP_TIMEOUT_MS;
    return {
        url: address.url,
        host: address.host,
        method,
        body,
        // A body with no declared type is JSON far more often than not in these
        // worlds, and an author who means something else says so.
        contentType: optStr(p, 'content_type', 'application/json'),
        timeoutMs: Math.min(Math.max(asked, HTTP_TIMEOUT_MIN), HTTP_TIMEOUT_MAX),
    };
}
/**
 * Make one request from inside the workspace and bring the answer back.
 *
 * The script goes to `node` through a quoted heredoc, so the shell expands
 * nothing in it and the only thing crossing into the guest is one JSON literal
 * written here — a learner's URL or body cannot become shell syntax, and it
 * cannot become a new line of the script either, because `JSON.stringify`
 * never emits a raw newline. The answer comes back on a single `@@HTTP=` line,
 * so a warning Node decides to print, or anything a profile echoes, cannot be
 * mistaken for a response.
 *
 * Every outcome is a value and none is an exception. A refused connection, a
 * timeout and a 500 are three different mistakes by the learner, and a check
 * that cannot tell them apart is a check that teaches nothing.
 *
 * `redirect: 'manual'` because a world that teaches status codes has to be
 * able to assert a 301. Following it would report the status of a *different*
 * request than the one the objective named.
 */
async function httpProbe(ctx, req) {
    const script = [
        `const req = ${JSON.stringify(req)};`,
        `const LIMIT = ${HTTP_BODY_LIMIT};`,
        // Exiting from the write callback rather than after it: stdout is a pipe,
        // a pipe write is asynchronous, and a body of any size would be cut off by
        // an immediate exit. The exit itself is not optional — undici holds the
        // connection open for reuse, and a probe that lingers for it spends the
        // learner's grading budget waiting for a socket nobody will use again.
        `const say = (o) => process.stdout.write(${JSON.stringify(HTTP_MARK)} + JSON.stringify(o) + '\\n', () => process.exit(0));`,
        `const KINDS = { ECONNREFUSED: 'refused', ECONNRESET: 'reset', EPIPE: 'reset', UND_ERR_SOCKET: 'reset',`,
        `  ENOTFOUND: 'unresolved', EAI_AGAIN: 'unresolved', EHOSTUNREACH: 'unreachable', ENETUNREACH: 'unreachable' };`,
        // The reason is never on the error itself — `fetch` rejects with a flat
        // "fetch failed" and hangs the truth off `cause`, and a host with two
        // addresses arrives as an AggregateError carrying one refusal per family.
        // So walk the chain, take the first thing recognised, and failing that
        // report the *deepest* message rather than the outermost, which is the
        // difference between telling an author "bad port" and telling them
        // nothing.
        `const describe = (e, depth) => {`,
        `  let deepest = '';`,
        `  for (let cur = e, i = 0; cur && i < 6; cur = cur.cause, i++) {`,
        `    if (typeof cur.message === 'string' && cur.message) deepest = cur.message;`,
        `    if (cur.name === 'TimeoutError' || cur.name === 'AbortError') return { kind: 'timeout', message: 'timed out' };`,
        `    if (typeof cur.code === 'string' && KINDS[cur.code]) return { kind: KINDS[cur.code], message: cur.code };`,
        // Not an error the learner can cause: fetch refuses a port on the WHATWG
        // blocked list before it opens a socket, so the author picked one.
        `    if (cur.message === 'bad port') return { kind: 'blocked', message: 'bad port' };`,
        `    if (Array.isArray(cur.errors) && (depth || 0) < 3) {`,
        `      for (const sub of cur.errors) { const d = describe(sub, (depth || 0) + 1); if (d.kind !== 'other') return d; }`,
        `    }`,
        `  }`,
        `  return { kind: 'other', message: deepest || String((e && e.name) || e) };`,
        `};`,
        `const init = { method: req.method, redirect: 'manual', signal: AbortSignal.timeout(req.timeoutMs) };`,
        `if (req.body !== undefined) { init.body = req.body; init.headers = { 'content-type': req.contentType }; }`,
        `let res;`,
        `try { res = await fetch(req.url, init); } catch (e) { const d = describe(e); say({ ok: false, kind: d.kind, message: d.message }); }`,
        `if (res) {`,
        `  const headers = {};`,
        `  for (const [k, v] of res.headers) headers[k] = headers[k] === undefined ? v : headers[k] + ', ' + v;`,
        `  try {`,
        `    const body = await res.text();`,
        `    const truncated = body.length > LIMIT;`,
        `    say({ ok: true, status: res.status, statusText: res.statusText || '', headers, body: truncated ? body.slice(0, LIMIT) : body, truncated });`,
        // The status is carried into the failure on purpose: "answered 200 and
        // then stopped sending" is a different bug from "never answered".
        `  } catch (e) { const d = describe(e); say({ ok: false, kind: d.kind, status: res.status, message: d.message }); }`,
        `}`,
    ].join('\n');
    const r = await ctx.exec(`node --input-type=module <<'ENGINEEROS_HTTP_PROBE'\n${script}\nENGINEEROS_HTTP_PROBE`);
    const line = r.stdout.split(/\r?\n/).reverse().find((l) => l.startsWith(HTTP_MARK));
    if (line === undefined) {
        const why = r.code === 124
            ? 'the request was still running when the grader stopped waiting'
            : r.stderr.split(/\r?\n/).find((l) => l.trim().length > 0)?.trim() ?? `node exited ${r.code} without answering`;
        return { ok: false, kind: 'runner', message: why };
    }
    try {
        return JSON.parse(line.slice(HTTP_MARK.length));
    }
    catch {
        return { ok: false, kind: 'runner', message: 'the probe answered something that is not JSON' };
    }
}
/** Colour and cursor sequences, which a tool that thinks it is on a terminal writes into its output. */
const ANSI = /\x1b\[[0-9;?]*[ -\/]*[@-~]/g;
/**
 * What a failed command printed, as evidence: stderr first, then stdout, the
 * newest part of each, within `max` characters together.
 *
 * Stderr first because that is where a crash goes — the `SyntaxError` a
 * leftover conflict marker produces, the stack a missing export throws — and
 * the tail of each because a test runner puts its summary last. When both are
 * long they share the room evenly, and whatever one does not need the other
 * gets: a two-line stderr over a page of TAP should cost two lines.
 *
 * Undefined when nothing was printed, which is the ordinary case for a check
 * written as a bare exit code and is not an empty string pretending to be
 * evidence.
 */
export function checkOutput(r, max = MAX_OUTPUT_CHARS) {
    const parts = [r.stderr, r.stdout]
        .map((text) => text.replace(ANSI, '').replace(/\r\n?/g, '\n').trim())
        .filter((text) => text.length > 0);
    if (parts.length === 0)
        return undefined;
    if (parts.length === 1)
        return clipEnd(parts[0], max);
    const [err, out] = parts;
    const room = max - 1;
    if (err.length + out.length <= room)
        return `${err}\n${out}`;
    let forErr = Math.min(err.length, Math.floor(room / 2));
    const forOut = Math.min(out.length, room - forErr);
    forErr = Math.min(err.length, room - forOut);
    return `${clipEnd(err, forErr)}\n${clipEnd(out, forOut)}`;
}
/** Seconds, as a person writes them: 5s, 0.5s. */
const seconds = (ms) => `${Math.round(ms / 100) / 10}s`;
/** What the response called itself: `404 Not Found`, or just `404`. */
const statusWords = (r) => (r.statusText ? `${r.status} ${r.statusText}` : `${r.status}`);
/**
 * Why no response came, in words that separate the mistakes.
 *
 * A refused connection means there is no server: the learner has not started
 * it, or it died on the way up, or it is on another port. A timeout means
 * there is one and it is stuck. Saying "the check failed" to both would send
 * half of them to read code that is not running.
 */
function httpFailureDetail(req, r) {
    const at = `${req.method} ${req.url}`;
    const host = req.host;
    switch (r.kind) {
        case 'refused':
            return `Nothing is listening on ${host}: the connection was refused. That is not a wrong answer — there is no server there to give one.`;
        case 'timeout':
            return r.status === undefined
                ? `${at} did not answer within ${seconds(req.timeoutMs)}, so the request was abandoned. Something is listening; it is not replying.`
                : `${at} answered ${r.status} and then stopped: the body had still not finished arriving after ${seconds(req.timeoutMs)}.`;
        case 'reset':
            return r.status === undefined
                ? `${host} accepted the connection and then closed it without answering (${r.message}). A server that crashes while handling a request looks exactly like this.`
                : `${at} answered ${r.status} and then closed the connection before the body finished.`;
        case 'unresolved':
            return `The host in "${req.url}" could not be resolved. There is no DNS here and no network device; only 127.0.0.1 ever answers.`;
        case 'unreachable':
            return `${host} cannot be reached from inside the workspace. There is no network device here; only 127.0.0.1 ever answers.`;
        case 'blocked':
            return `Nothing can fetch ${host}: that port is on the blocked list every browser and Node share (tcpmux, ssh, smtp and the rest), so the request is refused before a socket is opened. The level has to serve on another port.`;
        case 'runner':
            return `The request could not be made from inside the workspace: ${r.message}.`;
        default:
            return `${at} failed: ${r.message}.`;
    }
}
/** A body, shortened to something that fits inside a sentence. */
function preview(body, max = 120) {
    const flat = body.replace(/\s+/g, ' ').trim();
    if (flat.length === 0)
        return 'the body is empty';
    return `it starts ${JSON.stringify(flat.length > max ? `${flat.slice(0, max)}…` : flat)}`;
}
/** A JSON value as a detail should name it. */
const show = (v) => JSON.stringify(v) ?? 'undefined';
/** What kind of thing a value is, for the sentence that says it is the wrong kind. */
function shape(v) {
    if (v === null)
        return 'null';
    if (Array.isArray(v))
        return 'an array';
    const names = { string: 'a string', number: 'a number', boolean: 'a boolean', object: 'an object' };
    return names[typeof v] ?? typeof v;
}
/**
 * A value inside a JSON document, addressed the way a mission writes it:
 * `order.items.0.sku`, where a number is an array index.
 *
 * When it cannot get there it says where it stopped and what was there
 * instead, because "there is no `items.0.sku`" and "there is no `items` at
 * all" are two different mistakes, and a learner can only fix the one they are
 * actually making.
 */
function valueAtPath(doc, path) {
    const parts = path.split('.').filter((s) => s.length > 0);
    let cur = doc;
    for (let i = 0; i < parts.length; i++) {
        const key = parts[i];
        const walked = parts.slice(0, i).join('.');
        const at = walked.length === 0 ? 'the response' : `"${walked}"`;
        if (Array.isArray(cur)) {
            const idx = /^\d+$/.test(key) ? Number(key) : -1;
            if (idx < 0 || idx >= cur.length) {
                return { ok: false, detail: `${at} is an array of ${cur.length}, and there is no "${key}" in it.` };
            }
            cur = cur[idx];
            continue;
        }
        if (cur === null || typeof cur !== 'object') {
            return { ok: false, detail: `${at} is ${shape(cur)}, so there is nothing called "${key}" inside it.` };
        }
        const obj = cur;
        if (!(key in obj)) {
            const keys = Object.keys(obj);
            const has = keys.length === 0 ? 'it is empty' : `it has ${keys.slice(0, 8).map((k) => `"${k}"`).join(', ')}${keys.length > 8 ? ', …' : ''}`;
            return { ok: false, detail: `${at} has no "${key}" (${has}).` };
        }
        cur = obj[key];
    }
    return { ok: true, value: cur };
}
/**
 * Equality as a mission means it.
 *
 * Strict where it can be, and forgiving between a primitive and its own text,
 * because `{{seed.ORDER_TOTAL}}` arrives as a string however the seed printed
 * it: an author who writes `equals: "{{seed.STOCK}}"` is asking the same
 * question as one who writes `equals: 3`, and being told those differ would be
 * a lesson about YAML rather than about the API.
 */
function jsonEquals(actual, expected) {
    if (actual === expected)
        return true;
    const primitive = (v) => v === null || ['string', 'number', 'boolean'].includes(typeof v);
    if (primitive(actual) && primitive(expected))
        return String(actual) === String(expected);
    return JSON.stringify(actual) === JSON.stringify(expected);
}
const checks = {
    async branch_exists(ctx, p) {
        const branch = need(p, 'branch');
        const r = await ctx.git(`rev-parse -q --verify ${q(`refs/heads/${branch}`)}`);
        return r.code === 0 ? { ok: true } : { ok: false, detail: `Branch "${branch}" does not exist.` };
    },
    async head_on_branch(ctx, p) {
        const branch = need(p, 'branch');
        const cur = await currentBranch(ctx);
        if (cur === null)
            return { ok: false, detail: 'HEAD is detached; it is not on any branch.' };
        return cur === branch ? { ok: true } : { ok: false, detail: `HEAD is on "${cur}", not on "${branch}".` };
    },
    /** HEAD is attached to a branch that is not `base`. */
    async on_feature_branch(ctx, p) {
        const base = optStr(p, 'base', 'main');
        const cur = await currentBranch(ctx);
        if (cur === null)
            return { ok: false, detail: 'HEAD is detached; it is not on any branch.' };
        return cur === base ? { ok: false, detail: `You are still on "${base}".` } : { ok: true };
    },
    async commit_is_ancestor(ctx, p) {
        const commit = need(p, 'commit');
        const of = need(p, 'of');
        const exists = await ctx.git(`cat-file -e ${q(`${commit}^{commit}`)}`);
        if (exists.code !== 0) {
            return { ok: false, detail: `Commit ${commit.slice(0, 7)} no longer exists in this repository.` };
        }
        const r = await ctx.git(`merge-base --is-ancestor ${q(commit)} ${q(of)}`);
        if (r.code === 0)
            return { ok: true };
        if (r.code === 1)
            return { ok: false, detail: `Commit ${commit.slice(0, 7)} is not in the history of "${of}".` };
        return { ok: false, detail: `"${of}" could not be resolved.` };
    },
    /** The tree (full snapshot) at `ref` is identical to the tree of `commit`. */
    async tree_matches(ctx, p) {
        const ref = need(p, 'ref');
        const commit = need(p, 'commit');
        const a = await ctx.git(`rev-parse -q --verify ${q(`${ref}^{tree}`)}`);
        const b = await ctx.git(`rev-parse -q --verify ${q(`${commit}^{tree}`)}`);
        if (a.code !== 0 || b.code !== 0)
            return { ok: false, detail: `Could not resolve "${ref}" or the target commit.` };
        return a.stdout.trim() === b.stdout.trim()
            ? { ok: true }
            : { ok: false, detail: `The content at "${ref}" does not exactly match the expected snapshot.` };
    },
    async working_tree_clean(ctx, p) {
        const r = await ctx.git('status --porcelain');
        // A failed `git status` prints nothing to stdout, and reading that silence
        // as "no changes" graded a repository git could not even open as clean.
        // Every other check here treats git's exit code as the answer; this one
        // treated its stdout as the answer, which is only the same thing when git
        // succeeded.
        if (r.code !== 0) {
            return { ok: false, detail: 'Git could not read the state of the working tree here.' };
        }
        let lines = r.stdout.split(/\r?\n/).filter((l) => l.length > 0);
        if (p.ignore_untracked === true)
            lines = lines.filter((l) => !l.startsWith('??'));
        if (lines.length === 0)
            return { ok: true };
        const sample = lines.slice(0, 3).map((l) => l.slice(3)).join(', ');
        return { ok: false, detail: `The working tree has uncommitted changes (${sample}${lines.length > 3 ? ', …' : ''}).` };
    },
    async file_exists(ctx, p) {
        const path = need(p, 'path');
        const ref = typeof p.ref === 'string' ? p.ref : undefined;
        const c = await contentAt(ctx, path, ref);
        return c !== null ? { ok: true } : { ok: false, detail: `"${path}" does not exist ${where(ref)}.` };
    },
    async file_missing(ctx, p) {
        const path = need(p, 'path');
        const ref = typeof p.ref === 'string' ? p.ref : undefined;
        const c = await contentAt(ctx, path, ref);
        return c === null ? { ok: true } : { ok: false, detail: `"${path}" still exists ${where(ref)}.` };
    },
    async file_contains(ctx, p) {
        const path = need(p, 'path');
        const ref = typeof p.ref === 'string' ? p.ref : undefined;
        const c = await contentAt(ctx, path, ref);
        if (c === null)
            return { ok: false, detail: `"${path}" does not exist ${where(ref)}.` };
        return regex(p).test(c)
            ? { ok: true }
            : { ok: false, detail: `"${path}" ${where(ref)} does not contain the expected content.` };
    },
    async file_not_contains(ctx, p) {
        const path = need(p, 'path');
        const ref = typeof p.ref === 'string' ? p.ref : undefined;
        const c = await contentAt(ctx, path, ref);
        if (c === null)
            return { ok: false, detail: `"${path}" does not exist ${where(ref)}.` };
        return regex(p).test(c)
            ? { ok: false, detail: `"${path}" ${where(ref)} still contains the text that should be gone.` }
            : { ok: true };
    },
    /** Number of commits in `ref` that are not in `base`, bounded by min/max. */
    async commits_ahead(ctx, p) {
        const ref = optStr(p, 'ref', 'HEAD');
        const base = need(p, 'base');
        const r = await ctx.git(`rev-list --count ${q(`${base}..${ref}`)}`);
        if (r.code !== 0)
            return { ok: false, detail: `Could not compare "${ref}" with "${base}".` };
        const n = Number(r.stdout.trim());
        const min = optNum(p, 'min');
        const max = optNum(p, 'max');
        if (min !== undefined && n < min) {
            return { ok: false, detail: n === 0 ? `"${ref}" has no commits that "${base}" lacks.` : `"${ref}" is only ${n} commit(s) ahead of "${base}".` };
        }
        if (max !== undefined && n > max)
            return { ok: false, detail: `"${ref}" is ${n} commits ahead of "${base}"; expected at most ${max}.` };
        return { ok: true };
    },
    async no_merge_commits(ctx, p) {
        const ref = optStr(p, 'ref', 'HEAD');
        const base = need(p, 'base');
        const r = await ctx.git(`rev-list --merges ${q(`${base}..${ref}`)}`);
        return r.stdout.trim().length === 0 ? { ok: true } : { ok: false, detail: `"${ref}" contains merge commits; the history is not linear.` };
    },
    async last_commit_message(ctx, p) {
        const ref = optStr(p, 'ref', 'HEAD');
        const r = await ctx.git(`log -1 --format=%s ${q(ref)}`);
        if (r.code !== 0)
            return { ok: false, detail: `"${ref}" has no commits.` };
        return regex(p).test(r.stdout.trim())
            ? { ok: true }
            : { ok: false, detail: `The latest commit message on "${ref}" does not match what the ticket asks for.` };
    },
    async pull_request_exists(ctx, p) {
        const head = need(p, 'head');
        const state = optStr(p, 'state', 'open');
        const found = await findPull(ctx, p);
        if (!found.ok)
            return found;
        const inState = found.matches.filter((pr) => (pr.state ?? 'open') === state);
        if (inState.length === 0) {
            const seen = [...new Set(found.matches.map((pr) => pr.state ?? 'open'))].join(', ');
            return { ok: false, detail: `The pull request for "${head}" is ${seen}, not ${state}.` };
        }
        const base = typeof p.base === 'string' && p.base.length > 0 ? p.base : undefined;
        if (base !== undefined && !inState.some((pr) => pr.base === base)) {
            return { ok: false, detail: `The pull request for "${head}" targets "${inState[0]?.base ?? 'nothing'}", not "${base}".` };
        }
        return { ok: true };
    },
    /**
     * One string field of the pull request against a regex. Missions ask about
     * `title`, `body`, `base` and `merge_method`; any string field in the file
     * can be named.
     */
    async pull_request_field(ctx, p) {
        const head = need(p, 'head');
        const field = need(p, 'field');
        const found = await findPull(ctx, p);
        if (!found.ok)
            return found;
        const name = field.replace(/_/g, ' ');
        const value = found.pr[field];
        if (typeof value !== 'string')
            return { ok: false, detail: `The pull request for "${head}" has no ${name}.` };
        return regex(p).test(value)
            ? { ok: true }
            : { ok: false, detail: `The ${name} of the pull request for "${head}" does not match what the ticket asks for.` };
    },
    async pull_request_review(ctx, p) {
        const head = need(p, 'head');
        const state = need(p, 'state');
        const author = typeof p.author === 'string' && p.author.length > 0 ? p.author : undefined;
        const found = await findPull(ctx, p);
        if (!found.ok)
            return found;
        const reviews = (found.pr.reviews ?? []).filter((r) => author === undefined || r.author === author);
        if (reviews.some((r) => r.state === state))
            return { ok: true };
        const by = author === undefined ? '' : ` from "${author}"`;
        if (reviews.length === 0)
            return { ok: false, detail: `The pull request for "${head}" has no review${by}.` };
        const seen = [...new Set(reviews.map((r) => r.state ?? 'unrecorded'))].join(', ');
        return { ok: false, detail: `The reviews${by} on the pull request for "${head}" are ${seen}; none of them is ${state}.` };
    },
    async pull_request_comments(ctx, p) {
        const head = need(p, 'head');
        const path = typeof p.path === 'string' && p.path.length > 0 ? p.path : undefined;
        const resolved = typeof p.resolved === 'boolean' ? p.resolved : undefined;
        const found = await findPull(ctx, p);
        if (!found.ok)
            return found;
        const comments = (found.pr.comments ?? []).filter((c) => (path === undefined || c.path === path) && (resolved === undefined || (c.resolved === true) === resolved));
        const n = comments.length;
        const min = optNum(p, 'min');
        const max = optNum(p, 'max');
        const kind = resolved === undefined ? '' : resolved ? 'resolved ' : 'unresolved ';
        const on = path === undefined ? '' : ` on "${path}"`;
        const has = `The pull request for "${head}" has ${n} ${kind}comment(s)${on}`;
        if (min !== undefined && n < min)
            return { ok: false, detail: `${has}; expected at least ${min}.` };
        if (max !== undefined && n > max)
            return { ok: false, detail: `${has}; expected at most ${max}.` };
        return { ok: true };
    },
    /** Every check on the pull request, or the one named by `name`, at `status`. */
    async pull_request_checks(ctx, p) {
        const head = need(p, 'head');
        const status = need(p, 'status');
        const name = typeof p.name === 'string' && p.name.length > 0 ? p.name : undefined;
        const found = await findPull(ctx, p);
        if (!found.ok)
            return found;
        // The same merge `gh` does: what the file says, with what the hook
        // recorded against the head commit written over it, newest run winning.
        const byName = new Map();
        for (const c of found.pr.checks ?? [])
            byName.set(c.name ?? '', c);
        for (const c of await shaChecks(ctx, found.base.path, found.pr.head_sha?.trim() ?? ''))
            byName.set(c.name ?? '', c);
        const results = [...byName.values()].filter((c) => name === undefined || c.name === name);
        if (results.length === 0) {
            return {
                ok: false,
                detail: name === undefined
                    ? `Nothing has reported a check result on the pull request for "${head}".`
                    : `The pull request for "${head}" has no check called "${name}".`,
            };
        }
        const wrong = results.filter((c) => c.status !== status);
        if (wrong.length === 0)
            return { ok: true };
        const listed = wrong.slice(0, 3).map((c) => `"${c.name ?? 'unnamed'}" is ${c.status ?? 'unrecorded'}`).join(', ');
        return {
            ok: false,
            detail: `On the pull request for "${head}", ${listed}${wrong.length > 3 ? ', …' : ''}; expected ${status}.`,
        };
    },
    /**
     * `head_sha` in the file is still the tip of the head branch.
     *
     * The branch lives in whichever repository it was pushed to: the base
     * repository normally, and the fork (`origin`) when the pull request came
     * from one, so both are asked before the sha is called adrift.
     */
    async pull_request_at_head(ctx, p) {
        const head = need(p, 'head');
        const found = await findPull(ctx, p);
        if (!found.ok)
            return found;
        const recorded = typeof found.pr.head_sha === 'string' ? found.pr.head_sha.trim() : '';
        if (recorded.length === 0)
            return { ok: false, detail: `The pull request for "${head}" records no head commit.` };
        const dirs = [found.base.path];
        const fork = await remotePath(ctx, 'origin');
        if (fork !== null && fork !== found.base.path)
            dirs.push(fork);
        let tip = '';
        for (const dir of dirs) {
            const r = await ctx.git(`--git-dir=${q(dir)} rev-parse -q --verify ${q(`refs/heads/${head}`)}`);
            if (r.code === 0 && r.stdout.trim().length > 0) {
                tip = r.stdout.trim();
                break;
            }
        }
        if (tip.length === 0)
            return { ok: false, detail: `No branch "${head}" has been pushed to the repositories this pull request reads.` };
        if (tip === recorded || (recorded.length >= 7 && tip.startsWith(recorded)))
            return { ok: true };
        return {
            ok: false,
            detail: `The pull request for "${head}" is at ${recorded.slice(0, 7)}, while the branch is at ${tip.slice(0, 7)}.`,
        };
    },
    /** A release is a file in the base repository, like a pull request. */
    async release_exists(ctx, p) {
        const tag = need(p, 'tag');
        const base = await baseRepo(ctx);
        if (base === null)
            return NO_BASE;
        const r = await ctx.exec(`cat -- ${q(`${base.path}/releases/${tag}.json`)} 2>/dev/null`);
        if (r.code !== 0)
            return { ok: false, detail: `No release "${tag}" exists in "${base.remote}".` };
        if (typeof p.pattern !== 'string' || p.pattern.length === 0)
            return { ok: true };
        let notes = null;
        try {
            const obj = JSON.parse(r.stdout);
            // `gh release create -n` writes the notes into `body`, which is what
            // the API calls them; a seed that wrote `notes` means the same thing.
            for (const key of ['notes', 'body']) {
                if (typeof obj[key] === 'string') {
                    notes = obj[key];
                    break;
                }
            }
        }
        catch {
            return { ok: false, detail: `The release "${tag}" is not readable as a release.` };
        }
        if (notes === null)
            return { ok: false, detail: `The release "${tag}" has no notes.` };
        return regex(p).test(notes)
            ? { ok: true }
            : { ok: false, detail: `The notes on release "${tag}" do not say what the ticket asks for.` };
    },
    /**
     * Pushing `ref` to `remote` is refused.
     *
     * The push has to be real. `--dry-run` sends the remote no ref update at
     * all, so no `pre-receive` hook runs and every remote on earth looks
     * permissive. But a real push to the real remote moves it the moment
     * protection is off — which is the very case this check exists to fail, and
     * it would fail it by pushing to main.
     *
     * So the push is real and the remote is a throw-away: a copy of the bare
     * repository in a temp directory. `cp` is what makes the copy honest —
     * hooks, config and refs come with it, where `git clone` would drop the
     * hooks, which are the entire subject. The copy answers exactly as the
     * original would, and when the answer is yes the only thing that moved is a
     * directory this check then deletes.
     *
     * The copy's destination ref is wound back one commit first, because a push
     * to a ref already at that commit is "Everything up-to-date": git sends no
     * command, the hook never runs, and a protected branch would be graded as
     * open. Wound back, the copy sees exactly the update the learner's own push
     * would carry.
     *
     * Exit 90/91 keep "the copy could not be made" distinguishable from "the
     * push was refused"; `git push` uses neither.
     */
    async remote_rejects_push(ctx, p) {
        const remote = need(p, 'remote');
        const ref = need(p, 'ref');
        const path = await remotePath(ctx, remote);
        if (path === null)
            return { ok: false, detail: `There is no remote called "${remote}" here.` };
        const g = 'git --git-dir="$d/probe.git"';
        const script = [
            'd=$(mktemp -d) || exit 90',
            `cp -a ${q(path)} "$d/probe.git" || { rm -rf "$d"; exit 91; }`,
            // The destination half of the refspec: what protection is keyed on.
            `dst=${q(ref)}; dst=\${dst#+}; dst=\${dst##*:}`,
            `full=$(${g} rev-parse -q --verify --symbolic-full-name "$dst" 2>/dev/null || true)`,
            'if [ -n "$full" ]; then',
            `  back=$(${g} rev-parse -q --verify "$full^{commit}^" 2>/dev/null || true)`,
            `  if [ -n "$back" ]; then ${g} update-ref "$full" "$back"; else ${g} update-ref -d "$full"; fi`,
            'fi',
            `git push "$d/probe.git" ${q(ref)} >/dev/null 2>&1; c=$?`,
            'rm -rf "$d"',
            '[ "$c" -eq 0 ] && exit 0 || exit 1',
        ].join('\n');
        const r = await ctx.exec(script);
        if (r.code === 90 || r.code === 91) {
            return { ok: false, detail: `"${remote}" is not a repository on this disk, so nothing here can be pushed to it.` };
        }
        return r.code !== 0 ? { ok: true } : { ok: false, detail: `Pushing "${ref}" to "${remote}" is accepted; nothing there refuses it.` };
    },
    /**
     * One request, and every expectation the author attached to it.
     *
     * With neither `status` nor `body_matches` this is still worth writing: it
     * passes when the service answers at all, which is the entire objective of
     * the level whose job is to get it running.
     */
    async http_response(ctx, p) {
        // Everything the author wrote is read before anything is asked of the
        // service, so a mistyped parameter is a mistyped parameter whether or not
        // the server happens to be up.
        const req = httpRequest(p);
        const status = optInt(p, 'status');
        const pattern = optStr(p, 'body_matches', '');
        const wanted = pattern.length > 0 ? new RegExp(pattern, optStr(p, 'flags', '')) : null;
        const r = await httpProbe(ctx, req);
        if (!r.ok)
            return { ok: false, detail: httpFailureDetail(req, r) };
        if (status !== undefined && r.status !== status) {
            return { ok: false, detail: `${req.method} ${req.url} answered ${statusWords(r)}, not ${status}.` };
        }
        if (wanted !== null && !wanted.test(r.body)) {
            const cut = r.truncated ? ` The body is longer than the ${HTTP_BODY_LIMIT / 1024} KiB a check reads.` : '';
            return {
                ok: false,
                detail: `${req.method} ${req.url} answered ${statusWords(r)}, and the body is not what the ticket asks for (${preview(r.body)}).${cut}`,
            };
        }
        return { ok: true };
    },
    /**
     * One response header against a regex.
     *
     * Header names are matched without case, because the wire does not carry a
     * canonical one and a level must not turn on whether the learner wrote
     * `Content-Type` or `content-type`.
     */
    async http_header(ctx, p) {
        const req = httpRequest(p);
        const name = need(p, 'header').trim().toLowerCase();
        const wanted = regex(p);
        const r = await httpProbe(ctx, req);
        if (!r.ok)
            return { ok: false, detail: httpFailureDetail(req, r) };
        const value = r.headers[name];
        if (value === undefined) {
            const sent = Object.keys(r.headers);
            const list = sent.length === 0 ? 'it sent no headers at all' : `it sent ${sent.slice(0, 6).join(', ')}${sent.length > 6 ? ', …' : ''}`;
            return { ok: false, detail: `${req.method} ${req.url} answered ${statusWords(r)} with no "${name}" header; ${list}.` };
        }
        return wanted.test(value)
            ? { ok: true }
            : { ok: false, detail: `The "${name}" header on ${req.method} ${req.url} is "${value}", which is not what the ticket asks for.` };
    },
    /**
     * One value inside a JSON response, by path.
     *
     * This is the check for an API's *shape*: that the endpoint answers with a
     * document containing the field, under the name the contract gives it, with
     * the value the state implies. `equals` for a value the mission knows,
     * `matches` for one only its form is fixed — an id the learner's own code
     * generated, a timestamp, a message.
     */
    async http_json(ctx, p) {
        const req = httpRequest(p);
        const path = need(p, 'path');
        const wantsEquals = 'equals' in p && p.equals !== undefined;
        const matches = optStr(p, 'matches', '');
        if (!wantsEquals && matches.length === 0)
            throw new Error('http_json needs either "equals" or "matches"');
        const wanted = wantsEquals ? null : new RegExp(matches, optStr(p, 'flags', ''));
        const r = await httpProbe(ctx, req);
        if (!r.ok)
            return { ok: false, detail: httpFailureDetail(req, r) };
        let doc;
        try {
            doc = JSON.parse(r.body);
        }
        catch {
            // A body cut off at the limit fails to parse for a reason that is the
            // grader's, not the learner's, so say which one this was.
            const cut = r.truncated ? ` The body is longer than the ${HTTP_BODY_LIMIT / 1024} KiB a check reads, so it arrived cut off.` : '';
            return { ok: false, detail: `${req.method} ${req.url} answered ${statusWords(r)}, and the body is not JSON (${preview(r.body)}).${cut}` };
        }
        const found = valueAtPath(doc, path);
        const from = `in the response to ${req.method} ${req.url}`;
        if (!found.ok)
            return { ok: false, detail: `There is no "${path}" ${from}: ${found.detail}` };
        if (wantsEquals) {
            return jsonEquals(found.value, p.equals)
                ? { ok: true }
                : { ok: false, detail: `"${path}" ${from} is ${show(found.value)}, not ${show(p.equals)}.` };
        }
        const text = typeof found.value === 'string' ? found.value : show(found.value);
        return wanted.test(text)
            ? { ok: true }
            : { ok: false, detail: `"${path}" ${from} is ${show(found.value)}, which is not what the ticket asks for.` };
    },
    /**
     * Escape hatch for hidden checks: any script; exit code 0 passes.
     *
     * What the script printed is kept as `output` either way: on a failure it
     * explains the failure, and on a pass it is what a diagnostic that fired
     * saw (`CheckOutcome.output`). The detail stays the author's sentence.
     */
    async shell(ctx, p) {
        const run = need(p, 'run');
        const r = await ctx.exec(run);
        // The local and Docker drivers end an over-long command with 124 and a
        // marker; the browser rejects instead, which `runCheck` catches. Both
        // become the same honest failure rather than the author's sentence about
        // a behaviour nobody got to see.
        if (r.code === 124 && r.stderr.includes(TIMEOUT_MARKER))
            return timedOut();
        const output = checkOutput(r);
        if (r.code === 0)
            return { ok: true, ...(output ? { output } : {}) };
        return { ok: false, detail: optStr(p, 'detail', 'A behavioural check failed.'), ...(output ? { output } : {}) };
    },
    async any_of(ctx, p) {
        const specs = p.checks;
        if (!Array.isArray(specs) || specs.length === 0)
            throw new Error('any_of needs a non-empty "checks" list');
        const failures = [];
        for (const spec of specs) {
            const r = await runCheck(ctx, spec);
            if (r.ok)
                return { ok: true };
            if (r.timedOut)
                return r;
            failures.push(r);
        }
        // The output travels with the detail it explains: the first failure that
        // said something, which is the one the learner reads. Another branch's
        // output under this branch's sentence would be evidence for the wrong
        // claim.
        const shown = failures.find((r) => r.detail);
        const output = (shown ?? failures[0])?.output;
        return { ok: false, detail: shown?.detail, ...(output ? { output } : {}) };
    },
    async all_of(ctx, p) {
        const specs = p.checks;
        if (!Array.isArray(specs) || specs.length === 0)
            throw new Error('all_of needs a non-empty "checks" list');
        for (const spec of specs) {
            const r = await runCheck(ctx, spec);
            // The whole outcome, output included: the first sub-check to fail is
            // the objective's failure.
            if (!r.ok)
                return r;
        }
        return { ok: true };
    },
};
export const CHECK_TYPES = Object.keys(checks);
/**
 * How long one check's command may run. The guest agent's own backstop is
 * 120 s (packages/vm, `MAX_EXEC_TIMEOUT_MS`); this sits under it, so a check
 * that is slow on an emulated CPU gets time to finish rather than failing on
 * the learner's machine and passing on the author's. It was 30 s, and four
 * levels' reference solutions could not be graded in a tab inside it.
 */
export const CHECK_TIMEOUT_MS = 90_000;
/** What server/src/workspace/shell.ts appends to a command it had to stop. */
const TIMEOUT_MARKER = '[engineeros] process timed out after';
function timedOut() {
    const s = Math.round(CHECK_TIMEOUT_MS / 1000);
    return {
        ok: false,
        timedOut: true,
        detail: `This check did not finish within ${s} seconds, so it could not tell whether this part is done. ` +
            'Something in the code may be waiting for ever (a server left listening, a loop with no way out), ' +
            'or this machine may be busy. Run Check again once the code returns on its own.',
        output: `timed out after ${s} s: the check was stopped before it could decide`,
    };
}
/** A VM exec that ran out of time rejects rather than returning (packages/vm, `vmError('timeout', …)`). */
function isExecTimeout(err) {
    return err instanceof Error && err.code === 'timeout';
}
export async function runCheck(ctx, spec) {
    const fn = checks[spec.check];
    if (!fn)
        throw new Error(`Unknown check type "${spec.check}". Known: ${CHECK_TYPES.join(', ')}`);
    try {
        return await fn(ctx, spec.with ?? {});
    }
    catch (err) {
        // One slow objective is one failed objective. Before this, a timeout in a
        // tab threw out of the whole grade and the learner read an error quoting
        // the start of the check's script instead of any verdict at all.
        if (isExecTimeout(err))
            return timedOut();
        throw err;
    }
}
//# sourceMappingURL=checks.js.map